/**
 * Registro / entrar — ver `MEMBERSHIP_ROUTE.REGISTRO` y `MEMBERSHIP_ROUTE.ENTRAR`
 *
 * Post-pago: cuenta → `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO` → pendiente → mi acceso.
 * Rutas canónicas y portal: `membershipAccessFlow.ts`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { getRegistroClerkAppearance } from "@/lib/clerkRegistroAppearance";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { Loader2, LogIn, ShieldCheck, Sparkles, UserPlus } from "lucide-react";
import { useMemo } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { BRAND_PERSON_NAME, BRAND_SITE_URL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { REGISTRO_FUNNEL_COPY } from "@/lib/membershipFunnelEnglish";
import { useMembershipFunnelEnglish } from "@/hooks/useMembershipFunnelEnglish";

const t = REGISTRO_FUNNEL_COPY;

interface RegistroProps {
  /** Si "sign-in", muestra ingreso en lugar de signup */
  mode?: "sign-up" | "sign-in";
}

export default function Registro({ mode = "sign-up" }: RegistroProps) {
  useMembershipFunnelEnglish();
  useSEO({
    title: t.seoTitle,
    description: t.seoDescription(BRAND_PERSON_NAME),
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.REGISTRO}`,
    robots: "noindex, nofollow" as const,
  });
  const { search } = useLocation();
  const [params] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();

  const clerkAppearance = useMemo(() => getRegistroClerkAppearance(), []);

  // Preservar query params críticos para que /completar-registro los reciba
  const sessionId = params.get("session_id");
  const subscriptionId = params.get("subscription_id");
  const fromVip = params.get("from_special_offer") === "1" || params.get("from_vip") === "1";
  const planTier = (params.get("plan_tier") || "").trim().toLowerCase();
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" || (planTier === "standard" && !fromVip && !fromBasic);
  const fromMp = params.get("from_mp") === "true";
  /** Solicitud de acceso SIN pago (dual template VIP) → no afirmar "pago recibido". */
  const isAccessRequest = params.get("access_request") === "1";
  const variantId = params.get("variant_id")?.trim() ?? "";
  /**
   * return_to — landing path al que volver tras completar profile. Pasado por
   * MuxVideoPlayerWithCTA cuando gateamos el Stripe checkout: el visitor termina
   * el alta Clerk → CompleteRegistration lo manda back a esta URL → el CTA del
   * landing se reabre y entra a Stripe ya autenticado. Validamos solo rutas
   * locales (`/algo`) para evitar open-redirect.
   */
  const rawReturnTo = params.get("return_to")?.trim() ?? "";
  const returnTo =
    rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")
      ? rawReturnTo
      : "";

  const queryParts: string[] = ["lang=en"];
  if (fromVip) queryParts.push("from_special_offer=1");
  if (fromBasic) queryParts.push("from_basic=1");
  if (fromStandard) queryParts.push("from_standard=1");
  if (planTier) queryParts.push(`plan_tier=${encodeURIComponent(planTier)}`);
  if (variantId) queryParts.push(`variant_id=${encodeURIComponent(variantId)}`);
  if (fromMp) queryParts.push("from_mp=true");
  if (isAccessRequest) queryParts.push("access_request=1");
  if (sessionId) queryParts.push(`session_id=${encodeURIComponent(sessionId)}`);
  if (subscriptionId) queryParts.push(`subscription_id=${encodeURIComponent(subscriptionId)}`);
  if (returnTo) queryParts.push(`return_to=${encodeURIComponent(returnTo)}`);
  const query = queryParts.length ? `?${queryParts.join("&")}` : "";

  /** Tras iniciar sesión: misma corrida query que signup salvo ?next absoluto permitido */
  const rawNext = params.get("next")?.trim() ?? "";
  const redirectAfterSignIn =
    rawNext.startsWith("/") && !rawNext.startsWith("//")
      ? rawNext
      : `${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}${query}`;

  const forceRedirectUrl = `${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}${query}`;

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (isSignedIn) {
    // Si ya logueado, ir directo al completar registro (con los mismos query params)
    return <Navigate to={forceRedirectUrl} replace />;
  }

  const isSignUp = mode === "sign-up";
  const title = isSignUp ? t.signUpTitle : t.signInTitle;
  const subtitle = isSignUp
    ? isAccessRequest
      ? t.subtitleAccessRequest(BRAND_PERSON_NAME)
      : fromVip
        ? t.subtitlePaidVip(BRAND_PERSON_NAME)
        : fromBasic
          ? t.subtitlePaidBasic(BRAND_PERSON_NAME)
          : fromStandard
            ? t.subtitlePaidStandard(BRAND_PERSON_NAME)
            : t.subtitleDefault(BRAND_PERSON_NAME)
    : t.subtitleSignIn(BRAND_PERSON_NAME);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-10 sm:py-12">
      {/* Ambient glow — glass backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/15 blur-[120px]" />
        <div className="absolute bottom-[-10rem] right-[-6rem] h-80 w-80 rounded-full bg-amber-400/10 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.04),_transparent_60%)]" />
      </div>

      <div className="relative mx-auto w-full max-w-md">
        <Card className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-2xl shadow-black/60 ring-1 ring-inset ring-white/[0.06]">
          {/* Subtle top highlight for the glass edge */}
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <CardHeader className="text-center space-y-3 pb-4 pt-7">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-500/15 shadow-inner shadow-amber-500/10 backdrop-blur">
              {isSignUp ? (
                <UserPlus className="h-7 w-7 text-amber-300" />
              ) : (
                <LogIn className="h-7 w-7 text-amber-300" />
              )}
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight text-zinc-50">{title}</CardTitle>
            <CardDescription className="mx-auto max-w-sm text-sm leading-relaxed text-zinc-400">{subtitle}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 px-5 pb-6 pt-1 sm:px-6">
            {planTier === "free" ? (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-amber-100 backdrop-blur">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <p>
                  <strong className="text-amber-200">{t.badgeFreeTitle}</strong> {t.badgeFreeBody}
                </p>
              </div>
            ) : isAccessRequest ? (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-amber-100 backdrop-blur">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <p>
                  <strong className="text-amber-200">{t.badgeAccessRequestTitle}</strong>{" "}
                  {t.badgeAccessRequestBody}
                </p>
              </div>
            ) : (fromVip || fromBasic || fromStandard) && (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-amber-100 backdrop-blur">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <p>
                  {t.badgePaidPrefix}{" "}
                  <strong className="text-amber-200">After you create your account</strong>
                  {fromBasic || fromStandard ? t.badgePaidBasicSuffix : t.badgePaidVipSuffix}
                </p>
              </div>
            )}

            <div className="rounded-2xl border border-white/[0.07] bg-black/20 px-3 py-4 backdrop-blur sm:px-4">
              {isSignUp ? (
                <SignUp
                  appearance={clerkAppearance}
                  routing="hash"
                  forceRedirectUrl={forceRedirectUrl}
                  fallbackRedirectUrl={forceRedirectUrl}
                  signInUrl={search ? `${MEMBERSHIP_ROUTE.ENTRAR}${search}` : MEMBERSHIP_ROUTE.ENTRAR}
                  signInForceRedirectUrl={redirectAfterSignIn}
                  signInFallbackRedirectUrl={redirectAfterSignIn}
                />
              ) : (
                <SignIn
                  appearance={clerkAppearance}
                  routing="hash"
                  forceRedirectUrl={redirectAfterSignIn}
                  fallbackRedirectUrl={redirectAfterSignIn}
                  signUpUrl={search ? `${MEMBERSHIP_ROUTE.REGISTRO}${search}` : MEMBERSHIP_ROUTE.REGISTRO}
                  signUpForceRedirectUrl={forceRedirectUrl}
                  signUpFallbackRedirectUrl={forceRedirectUrl}
                />
              )}
            </div>

            <div className="pt-2 border-t border-white/10 flex items-center gap-2 text-xs text-zinc-500">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>{t.approvalFooter}</span>
            </div>

            <MembershipPortalHint className="pt-3 border-t border-white/10" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
